import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayView } from '@react-google-maps/api';

import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Map,
  Paper,
  Popover,
  ProfileAvatar,
  SegmentedFilter,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '../../../ui/core';
import { ChevronLeftIcon, SettingsIcon } from '../../../ui/icons';
import { rowHover, softFill } from '../../../ui/styles';
import { useFormatters, useLatenessThresholds, useLiveDrivers, useRideLive, useTripLive } from '../../../hooks';
import { validThresholds } from '../../../hooks/useLatenessThresholds';
import { DRIVER_STALE_MS } from '../../../hooks/useLiveDrivers';
import { buildTripStops } from './tripStops';
import TripLiveState, { latenessText, speedText } from './TripLiveState';
import { LiveDriverCard, OffTripDriverDot } from './LiveDriverCard';
import { getTripRoute } from '../../../redux/Trips/actions';
import MapFirstLayout, { fitPadding, PHONE_MAP_OPTIONS } from '../../shared/MapFirstLayout';
import MapErrorBoundary from '../../../pages/Rides/MapErrorBoundary';
import RideLiveMap, { ago, riderMarker, riderMarkers } from '../../../pages/Rides/RideLiveMap';
import { distanceMeters } from '../../../pages/Rides/driverGlide';
import RidePanel from '../../../pages/Rides/RidePanel';
import useRide from '../../../pages/Rides/useRide';
import {
  mapsReady,
  driverLateMinutes,
  latenessTier,
  spriteForLateness,
  spriteStyle,
} from '../../../pages/Rides/carSprite';
import { TripDisplayStatusIcon } from './TripDisplayStatus';
import { TripDisplayStatusOrder } from '../../../models/Trips';
import { RideStatusChip } from './RidersList';
import pickupSvg from '../../../assets/map/pickup.svg';
import dropoffSvg from '../../../assets/map/dropoff.svg';

// Austin, until there is something to fit.
const FALLBACK_CENTER = { lat: 30.2671, lng: -97.74308 };
const MAX_FIT_ZOOM = 15;

const rideIdOf = (ride) => ride.id || ride.rideId;
const fullName = (person) => (person ? `${person.firstName || ''} ${person.lastName || ''}`.trim() : '');

/** A full-bleed map that fits `points` (lat/lng) clear of the panel. */
const FittedMap = ({ points, sheetPeek, children }) => {
  const [map, setMap] = useState(null);
  const onLoad = useCallback((instance) => setMap(instance), []);
  const key = points.map((p) => `${p.lat},${p.lng}`).join('|');

  useEffect(() => {
    if (!map || !mapsReady() || !points.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, fitPadding(map, sheetPeek));
    if (map.getZoom() > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key, sheetPeek]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Map
        center={FALLBACK_CENTER}
        zoom={9}
        onLoad={onLoad}
        mapContainerStyle={{ width: '100%', height: '100%' }}
        options={{ gestureHandling: 'greedy', ...(sheetPeek && PHONE_MAP_OPTIONS) }}
      >
        {children}
      </Map>
    </Box>
  );
};

/** The route as the title, its departure under it. */
const TripTitle = ({ trip, departure }) => (
  <>
    <Typography variant="subtitle1" noWrap>
      {trip.originCity.cityName} ➔ {trip.destinationCity.cityName}
    </Typography>
    <Typography variant="body2" color="text.secondary" noWrap>
      {departure}
    </Typography>
  </>
);

/** Back arrow + title, at the top of each drilled-in panel. */
const PanelHeader = ({ onBack, backLabel, children }) => (
  <Stack direction="row" alignItems="center" spacing="4px">
    <IconButton size="small" onClick={onBack} aria-label={`Back to ${backLabel}`}>
      <ChevronLeftIcon />
    </IconButton>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Stack>
);

const useDeparture = (trip) => {
  const f = useFormatters();
  return f.date.toTimezoneLocaleString(
    trip.departureTime,
    trip.originCity.timeZone,
    f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
  );
};

// What's left for a rider on this trip, from their ride's status: still to be
// picked up, in the car waiting to be dropped off, or done.
const RIDER_STAGES = {
  pickup: { mark: '↑', label: 'to pick up', color: '#1E6FD9' },
  dropoff: { mark: '↓', label: 'to drop off', color: '#D93B3B' },
  done: { mark: '✓', label: 'dropped off', color: '#2E9E5B' },
};
const riderStage = (status) =>
  status === 'completed' ? RIDER_STAGES.done : status === 'en_route' ? RIDER_STAGES.dropoff : RIDER_STAGES.pickup;

/**
 * The trip's riders, one line each under the driver: their face (photo, else
 * their initial on their avatar colour) badged with what's left for them —
 * blue ↑ to pick up, red ↓ to drop off, green ✓ done — and their name.
 */
const RiderList = ({ rides }) => (
  // 12px between riders: the badge hangs 4px below each face.
  <Stack spacing="12px" sx={{ mt: '10px' }}>
    {rides.map((ride) => {
      const name = fullName(ride.rider) || 'Rider';
      const stage = riderStage(ride.status);
      return (
        // 16px: the badge hangs 5px past the face, so the name needs the extra room.
        <Stack key={rideIdOf(ride)} direction="row" alignItems="center" spacing="16px" sx={{ minWidth: 0 }}>
          <Tooltip title={`${name} · ${stage.label}`}>
            <Box sx={{ position: 'relative', display: 'flex', flexShrink: 0 }}>
              <ProfileAvatar name={name} photoUrl={ride.rider?.imageUrl} colorKey={name} size={24} />
              <Box
                aria-label={stage.label}
                sx={{
                  position: 'absolute',
                  right: -5,
                  bottom: -4,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: stage.color,
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 700,
                  lineHeight: '14px',
                  textAlign: 'center',
                  boxShadow: (theme) => `0 0 0 1.5px ${theme.palette.background.paper}`,
                }}
              >
                {stage.mark}
              </Box>
            </Box>
          </Tooltip>
          <Typography
            variant="body2"
            noWrap
            sx={{ minWidth: 0, color: stage === RIDER_STAGES.done ? 'text.secondary' : 'text.primary' }}
          >
            {name}
          </Typography>
        </Stack>
      );
    })}
  </Stack>
);

const TripRow = ({ trip, onClick }) => {
  const departure = useDeparture(trip);
  const rides = trip.rides || [];
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: 'none',
        background: 'none',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        borderRadius: '8px',
        padding: '10px 12px',
        '&:hover': { backgroundColor: rowHover },
      }}
    >
      {/* No status icon: the group header above names it. */}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {departure} · {trip.originCity.cityName} ➔ {trip.destinationCity.cityName}
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap>
        {fullName(trip.driver) || 'No driver'}
        {rides.length ? '' : ' · No riders'}
      </Typography>
      {rides.length > 0 && <RiderList rides={rides} />}
    </Box>
  );
};

/** Every trip on the day, as filtered on the page. */
// Smaller than the ride page's car: the board shows many at once.
const BOARD_PX = 40;

/** Amber / red for a forecast at risk / late, as on the board's cars. */
const TIER_COLOR = { at_risk: '#E39B2D', late: '#D93B3B' };

/**
 * This admin's amber / red thresholds, editable in place. Each field keeps
 * what's being typed and applies it as soon as the pair is valid (red after
 * amber); an invalid pair is outlined and not saved.
 */
const LatenessThresholdInputs = ({ thresholds, onChange }) => {
  const [draft, setDraft] = useState({ atRisk: String(thresholds.atRisk), late: String(thresholds.late) });
  // Follow an edit made elsewhere (another tab) without fighting the typing.
  useEffect(() => {
    setDraft({ atRisk: String(thresholds.atRisk), late: String(thresholds.late) });
  }, [thresholds.atRisk, thresholds.late]);
  const valid = validThresholds(draft);

  const edit = (field) => (e) => {
    const next = { ...draft, [field]: e.target.value };
    setDraft(next);
    if (validThresholds(next)) onChange(next);
  };

  const field = (key, label, color) => (
    <TextField
      size="small"
      type="number"
      value={draft[key]}
      onChange={edit(key)}
      error={!valid}
      inputProps={{ min: 0, step: 1, 'aria-label': `${label} after, minutes late` }}
      sx={{ width: 52, '& input': { py: '4px', px: '6px', fontSize: '13px' } }}
      InputProps={{ sx: { '& fieldset': { borderColor: color } } }}
    />
  );

  const row = (key, label, color) => (
    <Stack direction="row" alignItems="center" spacing="6px" sx={{ whiteSpace: 'nowrap' }}>
      <Typography variant="body2" sx={{ width: 84 }}>
        {label} after
      </Typography>
      {field(key, label, color)}
      <Typography variant="body2" color="text.secondary">
        min late
      </Typography>
    </Stack>
  );

  return (
    <Stack spacing="8px">
      {row('atRisk', 'Amber', TIER_COLOR.at_risk)}
      {row('late', 'Red', TIER_COLOR.late)}
    </Stack>
  );
};

/**
 * The board's settings, behind a gear beside the drivers filter. For now just
 * the car colours: when a late driver turns amber, then red.
 */
const BoardSettings = ({ thresholds, onChange }) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Box sx={{ p: '3px', borderRadius: '10px', backgroundColor: softFill }}>
        <IconButton
          size="small"
          aria-label="Map settings"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ width: 28, height: 28, borderRadius: '8px' }}
        >
          <SettingsIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: '6px', p: '14px', borderRadius: '12px' } }}
      >
        <Typography variant="subtitle2">Late drivers</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: '10px' }}>
          Car colour by how late the pick-up or return is forecast.
        </Typography>
        <LatenessThresholdInputs thresholds={thresholds} onChange={onChange} />
      </Popover>
    </>
  );
};

/** One forecast on the trip panel; nothing when there isn't one. */
const LatenessRow = ({ label, minutes, thresholds }) => {
  const text = latenessText(minutes);
  if (!text) return null;
  const tier = latenessTier(minutes, thresholds);
  return (
    <Stack direction="row" justifyContent="space-between" spacing="8px">
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: TIER_COLOR[tier] || 'text.primary', fontWeight: tier ? 600 : 400 }}>
        {text}
      </Typography>
    </Stack>
  );
};

/**
 * A live driver on a trip: the car, turned to its heading and coloured by how
 * late they're forecast to be, clickable through to the trip. (Drivers
 * between trips are OffTripDriverDot.)
 */
const LiveDriverMarker = ({ driver, trip, onOpenTrip, thresholds }) => {
  const name = fullName(trip?.driver);
  return (
    // The mouse-target pane, so the marker can take clicks.
    <OverlayView position={{ lat: driver.lat, lng: driver.lon }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
      <div
        role={trip ? 'button' : undefined}
        title={[
          `${name || 'Driver'} — on a trip`,
          speedText(driver),
          latenessTier(driver.pickupLateMinutes, thresholds) && `pick-up ${latenessText(driver.pickupLateMinutes)}`,
          latenessTier(driver.returnLateMinutes, thresholds) && `return ${latenessText(driver.returnLateMinutes)}`,
        ]
          .filter(Boolean)
          .join(' · ')}
        onClick={trip ? () => onOpenTrip(trip.id) : undefined}
        // `max-content`: the overlay's container is 0px wide, so a plain block
        // wrapper was 0px wide too, its -50% moved nothing sideways, and every
        // car drew half its width right of the driver.
        style={{ width: 'max-content', transform: 'translate(-50%, -50%)', cursor: trip ? 'pointer' : 'default' }}
      >
        <div
          style={spriteStyle(driver.bearing ?? 0, {
            ...spriteForLateness(driverLateMinutes(driver), thresholds),
            displayPx: BOARD_PX,
          })}
        />
      </div>
    </OverlayView>
  );
};

/**
 * Trips grouped like the list view's sections, in the same order, so the two
 * views read the same. Empty statuses are dropped; a status the order list
 * doesn't know is kept at the end rather than vanishing. Trip order within a
 * group is preserved.
 */
export const groupTripsByStatus = (trips) => {
  // A plain object, not `new Map()`: this file imports the Google `Map`
  // component, which shadows the built-in.
  const byStatus = {};
  trips.forEach((t) => {
    (byStatus[t.displayStatus] = byStatus[t.displayStatus] || []).push(t);
  });
  const seen = Object.keys(byStatus);
  const known = TripDisplayStatusOrder.filter((s) => byStatus[s]);
  const unknown = seen.filter((s) => !TripDisplayStatusOrder.includes(s));
  return [...known, ...unknown].map((status) => ({ status, trips: byStatus[status] }));
};

const TripsListLevel = ({ trips, loading, onSelectTrip, header }) => {
  const sections = useMemo(() => groupTripsByStatus(trips), [trips]);

  // Every live driver, on a trip or not. Open only at this level: selecting a
  // trip unmounts this and closes the stream, and the trip opens its own.
  const { drivers } = useLiveDrivers();
  const [thresholds, setThresholds] = useLatenessThresholds();
  const [driverFilter, setDriverFilter] = useState('on');
  // The driver whose card is open, followed by id so the card rides along as
  // their position updates — and closes if they drop off the board.
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const selectedDriver = selectedDriverId ? drivers[selectedDriverId] : null;
  const tripsById = useMemo(() => Object.fromEntries(trips.map((t) => [t.id, t])), [trips]);
  const liveDrivers = Object.values(drivers);
  const onTripCount = liveDrivers.filter((d) => d.tripId).length;
  const shownDrivers = liveDrivers.filter((d) =>
    driverFilter === 'all' ? true : driverFilter === 'on' ? Boolean(d.tripId) : !d.tripId,
  );

  // Frame the cities the day's trips run between, until live driver positions
  // give the map something better to show.
  const points = useMemo(() => {
    const seen = new Set();
    return trips
      .flatMap((t) => [t.originCity, t.destinationCity])
      .filter((c) => c?.cityCoord && !seen.has(c.id) && seen.add(c.id))
      .map((c) => ({ lat: c.cityCoord.coordinates[1], lng: c.cityCoord.coordinates[0] }));
  }, [trips]);

  return (
    <MapFirstLayout
      map={(sheetPeek) => (
        <MapErrorBoundary>
          <FittedMap points={points} sheetPeek={sheetPeek}>
            {shownDrivers.map((driver) =>
              driver.tripId ? (
                <LiveDriverMarker
                  key={driver.driverId}
                  driver={driver}
                  // Only a trip on this day's list can be opened from here.
                  trip={tripsById[driver.tripId]}
                  onOpenTrip={onSelectTrip}
                  thresholds={thresholds}
                />
              ) : (
                <OffTripDriverDot
                  key={driver.driverId}
                  driver={driver}
                  selected={driver.driverId === selectedDriverId}
                  onSelect={setSelectedDriverId}
                />
              ),
            )}
            {selectedDriver && !selectedDriver.tripId && (
              <LiveDriverCard driver={selectedDriver} onClose={() => setSelectedDriverId(null)} />
            )}
          </FittedMap>
        </MapErrorBoundary>
      )}
    >
      {/* The page header (filters, date, the view toggle) on a phone, where the
          map takes the whole screen. Edge to edge, like it sits on the page. */}
      {header && <Box sx={{ mx: -2 }}>{header}</Box>}
      <Typography variant="subtitle1" sx={{ px: '12px' }}>
        {trips.length} {trips.length === 1 ? 'trip' : 'trips'}
      </Typography>
      {loading && <LinearProgress />}
      {liveDrivers.length > 0 && (
        <Stack direction="row" spacing="8px" alignItems="center" sx={{ px: '12px' }}>
          <Box sx={{ flex: 1 }}>
            <SegmentedFilter
              value={driverFilter}
              onChange={setDriverFilter}
              segments={[
                { key: 'all', label: `All (${liveDrivers.length})` },
                { key: 'on', label: `On trip (${onTripCount})` },
                { key: 'off', label: `Off trip (${liveDrivers.length - onTripCount})` },
              ]}
            />
          </Box>
          <BoardSettings thresholds={thresholds} onChange={setThresholds} />
        </Stack>
      )}
      {sections.map(({ status, trips: group }) => (
        <Box key={status}>
          {/* Sticky within the panel's own scroll, so the status stays named
              while you scroll its trips; opaque so headers cover each other. */}
          <Stack
            direction="row"
            alignItems="center"
            spacing="8px"
            sx={{
              position: 'sticky',
              // Sticky pins at the scroller's padding edge, so the desktop
              // panel's 16px top padding (MapFirstLayout `p: 2`) left a strip
              // trips scrolled through above the header. Pin to the panel's
              // top edge instead. The phone sheet's scroller has no top padding.
              top: { xs: 0, md: '-16px' },
              zIndex: 1,
              px: '12px',
              py: '8px',
              backgroundColor: (theme) => theme.palette.background.paper,
            }}
          >
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <TripDisplayStatusIcon status={status} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
              {status}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({group.length})
            </Typography>
          </Stack>
          <Stack spacing="2px">
            {group.map((trip) => (
              <TripRow key={trip.id} trip={trip} onClick={() => onSelectTrip(trip.id)} />
            ))}
          </Stack>
        </Box>
      ))}
    </MapFirstLayout>
  );
};

/**
 * A stop's ETA, or where it stands. Times are in the trip's origin zone, like
 * the rest of the Trips page.
 */
const StopStatus = ({ stop, timeZone }) => {
  const f = useFormatters();
  if (stop.done) {
    return (
      <Typography variant="body2" color="text.secondary">
        Done
      </Typography>
    );
  }
  if (!stop.eta) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Typography variant="body2" sx={{ fontWeight: stop.next ? 600 : 400 }}>
      {f.date.toTimezoneLocaleString(stop.eta, timeZone, f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE)}
    </Typography>
  );
};

const STEP_LABEL = { 'pick-up': 'Pick-up', 'drop-off': 'Drop-off' };

/** "~70 m" up close, "1.4 mi" further out. */
const distanceText = (meters) =>
  meters < 1000 ? `~${Math.max(10, Math.round(meters / 10) * 10)} m` : `${(meters / 1609.34).toFixed(1)} mi`;

/**
 * The trip stream's waiting riders, as the map draws them: named from the
 * day's ride data. Memoised on the stream's list so the map isn't handed a new
 * array — and re-framed — on every render.
 */
const useRiderMarkers = (liveRiders, rides) =>
  useMemo(() => {
    const rideById = Object.fromEntries((rides || []).map((r) => [rideIdOf(r), r]));
    return (liveRiders || []).map((r) => riderMarker(r, rideById[r.rideId]?.rider));
  }, [liveRiders, rides]);

/**
 * One trip, live: the car on the route it is following, every stop (passed
 * ones faded), the ETA to each stop still ahead, and each ride's status.
 *
 * Everything moving comes off `/admin/live/trip/:tripId`. The day's trip data
 * fills in what the stream doesn't carry (names, places) and stands in until
 * the stream's first snapshot — or when it can't connect — so the view never
 * goes blank.
 */
const TripLevel = ({ trip, onBack, onSelectRide, onOpenTrip }) => {
  const dispatch = useDispatch();
  const departure = useDeparture(trip);
  const live = useTripLive(trip.id);
  const [thresholds] = useLatenessThresholds();
  const riders = useRiderMarkers(live.riders, trip.rides);
  const riderByRide = useMemo(() => Object.fromEntries(riders.map((r) => [r.rideId, r])), [riders]);
  const savedPolyline = useSelector((state) => state.trips.tripRoutes[trip.id]?.polyline);

  // A trip with no driver yet shows the drivers between trips — anyone who
  // reported in the last 15 minutes — so ops can see who's near enough to
  // take it. The drivers stream is only opened for such a trip.
  const needsDriver = !trip.driver;
  const { drivers: liveDrivers } = useLiveDrivers({ enabled: needsDriver });
  const offTripDrivers = useMemo(
    () => (needsDriver ? Object.values(liveDrivers).filter((d) => !d.tripId) : []),
    [needsDriver, liveDrivers],
  );
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const selectedDriver = selectedDriverId ? liveDrivers[selectedDriverId] : null;

  // The saved route, until (or unless) the stream has the live one.
  useEffect(() => {
    getTripRoute(trip.id)(dispatch);
  }, [trip.id, dispatch]);

  const liveTrip = live.trip;
  const waypoints = liveTrip?.waypoints ?? trip.waypoints;
  const currentWaypoint = liveTrip?.currentWaypoint ?? trip.currentWaypoint ?? 0;
  const completed = (liveTrip?.status ?? trip.status) === 'completed';
  const stops = useMemo(
    () => buildTripStops({ waypoints, currentWaypoint, completed, etas: live.etas, rides: trip.rides || [] }),
    [waypoints, currentWaypoint, completed, live.etas, trip.rides],
  );

  const firstPickupAhead = useMemo(() => {
    const stop = stops.find((s) => !s.done && s.lat !== null && s.steps.some((step) => step.type === 'pick-up'));
    return stop ? { lat: stop.lat, lng: stop.lng } : null;
  }, [stops]);

  // A fix older than the drivers board's cutoff is a parked phone, not a car.
  const fix = live.driver && Date.now() - Date.parse(live.driver.timestamp) <= DRIVER_STALE_MS ? live.driver : null;
  const mapState = useMemo(
    () => ({
      tripId: trip.id,
      routePolyline: live.route?.encodedPolyline || savedPolyline || null,
      driverLocation: fix ? { lat: fix.lat, lon: fix.lon, bearing: fix.bearing } : null,
    }),
    [trip.id, live.route, savedPolyline, fix],
  );

  const liveStatus = useMemo(
    () => Object.fromEntries((liveTrip?.rides || []).map((r) => [r.id, r.status])),
    [liveTrip],
  );

  return (
    <MapFirstLayout
      map={(sheetPeek) => (
        <MapErrorBoundary>
          <RideLiveMap
            state={mapState}
            stops={stops}
            riders={riders}
            sprite={spriteForLateness(driverLateMinutes(fix), thresholds)}
            sheetPeek={sheetPeek}
          >
            {offTripDrivers.map((driver) => (
              <OffTripDriverDot
                key={driver.driverId}
                driver={driver}
                selected={driver.driverId === selectedDriverId}
                onSelect={setSelectedDriverId}
              />
            ))}
            {selectedDriver && !selectedDriver.tripId && (
              <LiveDriverCard
                driver={selectedDriver}
                // Distance to the first pick-up still ahead: who could get there.
                target={firstPickupAhead}
                sheetPeek={sheetPeek}
                onClose={() => setSelectedDriverId(null)}
              />
            )}
          </RideLiveMap>
        </MapErrorBoundary>
      )}
    >
      <PanelHeader onBack={onBack} backLabel="all trips">
        <TripTitle trip={trip} departure={departure} />
      </PanelHeader>

      <Paper variant="outlined" sx={{ padding: '12px 16px' }}>
        <Stack spacing="6px">
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing="8px">
            <Typography color="text.secondary" variant="body2">
              Status
            </Typography>
            <Stack direction="row" spacing="6px" alignItems="center">
              <TripDisplayStatusIcon status={trip.displayStatus} />
              <Typography variant="body2">{trip.displayStatus}</Typography>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing="8px">
            <Typography color="text.secondary" variant="body2">
              Driver
            </Typography>
            <Typography variant="body2">
              {[fullName(trip.driver) || '—', fix && speedText(fix)].filter(Boolean).join(' · ')}
            </Typography>
          </Stack>
          {/* The location service's forecasts — to this trip's first pick-up
              while it's ahead, and for the return after it — coloured like
              the car when at risk or late. */}
          <LatenessRow label="Pick-up" minutes={fix?.pickupLateMinutes} thresholds={thresholds} />
          <LatenessRow label="Return" minutes={fix?.returnLateMinutes} thresholds={thresholds} />
          <Button size="small" onClick={onOpenTrip} sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}>
            Open full trip
          </Button>
        </Stack>
      </Paper>

      {stops.length > 0 && (
        <Paper variant="outlined" sx={{ padding: '8px' }}>
          <Typography variant="subtitle1" sx={{ px: '8px', pt: '4px' }}>
            Stops
          </Typography>
          {stops.map((stop) => (
            <Stack
              key={stop.index}
              direction="row"
              spacing="10px"
              alignItems="flex-start"
              sx={{ padding: '8px', opacity: stop.done ? 0.55 : 1 }}
            >
              <Box
                component="img"
                src={stop.steps[0]?.type === 'pick-up' ? pickupSvg : dropoffSvg}
                alt=""
                sx={{ width: 18, height: 18, mt: '2px', flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: stop.next ? 600 : 400 }} noWrap>
                  {stop.placeName || 'Stop'}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {stop.steps
                    .map((step) => `${STEP_LABEL[step.type] || step.type} ${fullName(step.rider) || 'rider'}`)
                    .join(' · ')}
                </Typography>
                {/* Where each rider being picked up here last said they were. */}
                {!stop.done &&
                  stop.lat !== null &&
                  stop.steps
                    .filter((step) => step.type === 'pick-up' && riderByRide[step.rideId])
                    .map((step) => {
                      const rider = riderByRide[step.rideId];
                      const away = distanceMeters({ lat: rider.lat, lng: rider.lng }, { lat: stop.lat, lng: stop.lng });
                      return (
                        <Typography key={step.rideId} variant="body2" sx={{ color: '#7C4DFF' }} noWrap>
                          {rider.name} {distanceText(away)} away · {ago(rider.timestamp)}
                        </Typography>
                      );
                    })}
              </Box>
              <StopStatus stop={stop} timeZone={trip.originCity.timeZone} />
            </Stack>
          ))}
        </Paper>
      )}

      <Paper variant="outlined" sx={{ padding: '8px' }}>
        <Typography variant="subtitle1" sx={{ px: '8px', pt: '4px' }}>
          Rides
        </Typography>
        {(trip.rides || []).length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: '8px' }}>
            No rides on this trip.
          </Typography>
        )}
        {(trip.rides || []).map((ride) => (
          <Box
            key={rideIdOf(ride)}
            component="button"
            type="button"
            onClick={() => onSelectRide(rideIdOf(ride))}
            sx={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              border: 'none',
              background: 'none',
              font: 'inherit',
              color: 'inherit',
              cursor: 'pointer',
              borderRadius: '8px',
              padding: '8px',
              '&:hover': { backgroundColor: rowHover },
            }}
          >
            <Stack direction="row" spacing="8px" alignItems="center">
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }} noWrap>
                {fullName(ride.rider) || 'Rider'}
              </Typography>
              <RideStatusChip status={liveStatus[rideIdOf(ride)] || ride.status} />
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap>
              {ride.pickUp?.name || ride.pickUp?.address || '—'} ➔ {ride.dropOff?.name || ride.dropOff?.address || '—'}
            </Typography>
          </Box>
        ))}
      </Paper>

      <Paper variant="outlined" sx={{ padding: '12px 16px' }}>
        <TripLiveState live={live} />
      </Paper>
    </MapFirstLayout>
  );
};

/** One ride: exactly the ride page, with a way back to its trip. */
const RideLevel = ({ rideId, trip, onBack }) => {
  // The same stream the rider's app is on — see RideLiveState. It also carries
  // where the rider is, while the ride waits for pick-up.
  const live = useRideLive(rideId);
  const person = (trip.rides || []).find((r) => rideIdOf(r) === rideId)?.rider;
  const riders = useMemo(() => riderMarkers(live.rider, person), [live.rider, person]);
  const { ride, status } = useRide(rideId);
  const departure = useDeparture(trip);

  return (
    <MapFirstLayout
      map={(sheetPeek) => (
        <MapErrorBoundary>
          <RideLiveMap state={live.state} riders={riders} sheetPeek={sheetPeek} />
        </MapErrorBoundary>
      )}
    >
      <PanelHeader onBack={onBack} backLabel="trip">
        <TripTitle trip={trip} departure={departure} />
      </PanelHeader>
      {status === 'loading' && <LinearProgress />}
      {status === 'notFound' && <Typography color="text.secondary">There is no ride with id {rideId}.</Typography>}
      {status === 'error' && <Typography color="error.main">There was a problem fetching this ride.</Typography>}
      {status === 'ready' && <RidePanel ride={ride} live={live} />}
    </MapFirstLayout>
  );
};

/**
 * The Trips page, map first: the day's trips → one trip's rides → one ride's
 * live view, each a level of the same panel. The selection lives in the URL
 * (`tripId`, `rideId`) so every level can be linked to and Back steps up one.
 */
const TripsMapView = ({ trips, allTrips, loading, tripId, rideId, onNavigate, listHeader }) => {
  const trip = tripId ? allTrips.find((t) => t.id === tripId) : null;

  if (trip && rideId) {
    return <RideLevel rideId={rideId} trip={trip} onBack={() => onNavigate({ tripId, rideId: undefined })} />;
  }
  if (trip) {
    return (
      <TripLevel
        trip={trip}
        onBack={() => onNavigate({ tripId: undefined, rideId: undefined })}
        onSelectRide={(id) => onNavigate({ tripId, rideId: id })}
        onOpenTrip={() => onNavigate({ view: 'list', tripId, fullScreen: true })}
      />
    );
  }
  return (
    <TripsListLevel
      trips={trips}
      header={listHeader}
      loading={loading}
      onSelectTrip={(id) => onNavigate({ tripId: id, rideId: undefined })}
    />
  );
};

export default TripsMapView;
